import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";

export const BlogLeadCapture = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) return;

    // Trigger CRM tracking event
    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "Pricing Guide Lead Capture",
      formData: {
        first_name: name,
        email: email,
        phone: phone,
      },
      formLabels: {
        first_name: "Name",
        email: "Email",
        phone: "Phone",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_12d30557e33f4e29be39cf7c348f6d99",
      locationId: "M7JmlFFXdtFNLwyfY61D",
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "desktop",
      },
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    setSubmitted(true);
    toast({
      title: "Guide Sent!",
      description: "Check your email for your exclusive pricing guide and discount code.",
    });
  };

  if (submitted) {
    return (
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center my-12 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/20 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-bold mb-2">You're all set!</h3>
        <p className="text-muted-foreground">We've sent the detailed pricing guide to your email.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border shadow-lg rounded-2xl p-8 my-12 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-32 -mt-32"></div>
      
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 text-primary rounded-lg">
            <Mail className="w-5 h-5" />
          </div>
          <h3 className="text-2xl font-bold">Get the Full 2026 Pricing Guide PDF</h3>
        </div>
        
        <p className="text-muted-foreground mb-6">
          Enter your details below to instantly download our comprehensive room-by-room pricing breakdown and receive a $50 coupon for your first deep clean.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="lead-name">First Name</Label>
              <Input 
                id="lead-name" 
                placeholder="John" 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-phone">Phone (Optional)</Label>
              <Input 
                id="lead-phone" 
                type="tel"
                placeholder="(555) 123-4567" 
                value={phone} 
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-email">Email Address</Label>
            <Input 
              id="lead-email" 
              type="email" 
              placeholder="john@example.com" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full h-12 text-lg font-bold group mt-2">
            Send Me The Guide <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-3">
            By submitting this form, you agree to our <a href="/privacy-policy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and consent to be contacted regarding your request.
          </p>
        </form>
      </div>
    </div>
  );
};
